<script setup>

import {computed, onMounted, reactive, ref, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import {useI18n} from "vue-i18n";
import LoadingIcon from "@/components/LoadingIcon.vue";
import Paginate from "vuejs-paginate-next"
import {accessControlApi, crawl_sites_Api, crawl_articles_Api} from "@/api/forwriting_index";
import {datePipe} from "@/utils/datatimeUtils";
import CheckPointLimit from "@/views/forwriting/fw00_check_point_limit.vue"

const route = useRoute()
const router = useRouter();
const { t } = useI18n();
const article_list_id = route.params.article_list_id

// ==== data state ====

const PAGE_NUM_OPTIONS = [10, 25, 50, 100]

const data = reactive({
  items: [],
  total_count: 0,
  current_page: 1,
  page_num: 25,
  sort_by: "created_at",
  sort_order: "desc",
})

const handlePageNumChange = async () => {
  data.current_page = 1
  await reload_data()
}

const filter = reactive({
  search_keyword: "",
  search_keyword_temp: "",
  crawl_site_id: null,
  status: "new",
})

const crawl_sites_list = ref([])

// ==== pagination ====

const page_count = computed(() => {
  const total = data.total_count || 0
  const pageNum = data.page_num || 10
  return total === 0 || pageNum === 0 ? 1 : Math.ceil(total / pageNum)
})

watch(() => data.current_page, async (newPage, oldPage) => {
  if (oldPage !== undefined && newPage !== oldPage && newPage > 0) {
    await reload_data()
  }
})

// ==== load data ====

const reload_data = async () => {
  const limit = data.page_num
  const offset = (data.current_page - 1) * limit

  const resp = await crawl_articles_Api.getAll_filter(
      article_list_id,
      data.sort_by,
      data.sort_order,
      limit,
      offset,
      filter.search_keyword || null,
      filter.crawl_site_id || null,
      filter.status,
  )
  data.items = resp.data.items
  data.total_count = resp.data.total

  // reset checked
  checked.items = {}
  for (let item of data.items) {
    checked.items[item.id] = false
  }
}

const load_crawl_sites = async () => {
  const resp = await crawl_sites_Api.getAll_filter(article_list_id)
  crawl_sites_list.value = resp.data
}

onMounted(async () => {
  if (!await accessControlApi.check_option_access()) {
    await router.push(`/forwriting/options_error`)
    return
  }
  await load_crawl_sites()
  await reload_data()
  point_limit.is_over = await ref_CheckPointLimit.value.check()
})

// ==== status tab ====

const STATUS_TABS = computed(() => [
  { value: "new",       label: t("Crawl_articles.status_new") },
  { value: "added",     label: t("Crawl_articles.status_added") },
  { value: "dismissed", label: t("Crawl_articles.status_dismissed") },
  { value: "all",       label: t("Crawl_articles.status_all") },
])

const handleStatusTab = async (val) => {
  filter.status = val
  data.current_page = 1
  await reload_data()
}

// ==== filter actions ====

const handleFilter = async () => {
  filter.search_keyword = filter.search_keyword_temp
  data.current_page = 1
  await reload_data()
}

const handleSiteFilter = async () => {
  data.current_page = 1
  await reload_data()
}

// ==== checked/un-checked ====

const checked = reactive({
  items: {}
})
const onSelectAllChange = (e) => {
  const val = e.target.checked
  for (const i in checked.items)
    checked.items[i] = val
}
const getCheckedIds = () => {
  const ids = []
  for (const id in checked.items) {
    if (checked.items[id]) ids.push(parseInt(id))
  }
  return ids
}

// ==== modal_update ====

const modal_update = reactive({
  show: false,
  id: -1,
  article: {
    title: "",
    url: "",
    match_score: null,
    match_reason: "",
    site_name: "",
    status: "new",
  },
})
const handleEdit = async (par_id) => {
  modal_update.id = par_id
  const resp = await crawl_articles_Api.get_1(par_id)
  const d = resp.data
  modal_update.article.title = d.title || ""
  modal_update.article.url = d.url || ""
  modal_update.article.match_score = d.match_score
  modal_update.article.match_reason = d.match_reason || ""
  modal_update.article.site_name = d.site_name || ""
  modal_update.article.status = d.status || "new"
  modal_update.show = true
}
const handleEdit_confirm = async () => {
  isLoading.show = true;
  try {
    await crawl_articles_Api.update(modal_update.id, {
      title: modal_update.article.title,
      url: modal_update.article.url,
    })
    await reload_data()
    modal_update.show = false
  } catch (error) {
    console.error("Update crawl article failed:", error);
    alert(t("Crawl_articles.update_failed"))
  }
  isLoading.show = false;
}

// ==== delete single ====

const modal_del_1 = reactive({
  show: false,
  msg: "",
  id: -1,
})
const handleDelete = (par_id, title) => {
  modal_del_1.msg = t("Crawl_articles.delete_confirm", { title })
  modal_del_1.id = par_id
  modal_del_1.show = true
}
const handleDelete_confirm = async () => {
  isLoading.show = true;
  try {
    await crawl_articles_Api.delete(modal_del_1.id)
    await reload_data()
    modal_del_1.show = false
  } catch (error) {
    alert(t("Crawl_articles.delete_failed"))
  }
  isLoading.show = false;
}

// ==== dismiss single ====

const handleDismiss = async (par_id) => {
  isLoading.show = true;
  try {
    await crawl_articles_Api.dismiss(par_id)
    await reload_data()
  } catch (error) {
    alert(t("Crawl_articles.dismiss_failed"))
  }
  isLoading.show = false;
}

// ==== dismiss from modal ====

const handleDismissFromModal = async () => {
  isLoading.show = true;
  try {
    await crawl_articles_Api.dismiss(modal_update.id)
    await reload_data()
    modal_update.show = false
  } catch (error) {
    alert(t("Crawl_articles.dismiss_failed"))
  }
  isLoading.show = false;
}

// ==== bulk add to articles ====

const handleBulkAdd = async () => {
  const ids = getCheckedIds()
  if (ids.length === 0) {
    alert(t("Crawl_articles.select_article"))
    return
  }
  isLoading.show = true;
  try {
    const resp = await crawl_articles_Api.bulk_add(article_list_id, ids)
    if (resp.data && resp.data.created_article_ids) {
      alert(t("Crawl_articles.bulk_add_success", { count: resp.data.created_article_ids.length }))
      await reload_data()
    }
  } catch (error) {
    console.error("Bulk add crawl articles failed:", error);
    alert(t("Crawl_articles.add_failed"))
  }
  isLoading.show = false;
}

// ==== add single to articles (from modal) ====

const handleAddSingleToArticles = async () => {
  isLoading.show = true;
  try {
    await crawl_articles_Api.add_to_articles(modal_update.id)
    alert(t("Crawl_articles.add_success"))
    modal_update.show = false
    await reload_data()
  } catch (error) {
    console.error("Add crawl article failed:", error);
    alert(t("Crawl_articles.add_failed"))
  }
  isLoading.show = false;
}

// ==== delete from modal ====

const handleDeleteFromModal = async () => {
  isLoading.show = true;
  try {
    await crawl_articles_Api.delete(modal_update.id)
    await reload_data()
    modal_update.show = false
  } catch (error) {
    alert(t("Crawl_articles.delete_failed"))
  }
  isLoading.show = false;
}

// ==== bulk dismiss ====

const handleBulkDismiss = async () => {
  const ids = getCheckedIds()
  if (ids.length === 0) {
    alert(t("Crawl_articles.select_article"))
    return
  }
  isLoading.show = true;
  try {
    await crawl_articles_Api.bulk_dismiss(article_list_id, ids)
    await reload_data()
  } catch (error) {
    alert(t("Crawl_articles.dismiss_failed"))
  }
  isLoading.show = false;
}

// ==== bulk delete ====

const handleBulkDelete = async () => {
  const ids = getCheckedIds()
  if (ids.length === 0) {
    alert(t("Crawl_articles.select_article"))
    return
  }
  if (!confirm(t("Crawl_articles.bulk_delete_confirm", { count: ids.length }))) return

  isLoading.show = true;
  try {
    await crawl_articles_Api.bulk_delete(article_list_id, ids)
    await reload_data()
  } catch (error) {
    alert(t("Crawl_articles.delete_failed"))
  }
  isLoading.show = false;
}

// ==== refresh (list reload only, no crawl) ====

const handleRefresh = async () => {
  isLoading.show = true;
  try {
    await reload_data()
  } catch (error) {
    alert(t("Crawl_articles.refresh_failed"))
  }
  isLoading.show = false;
}

// ==== isLoading ====

const isLoading = reactive({
  show: false
})

// ==== modal_over_points ====
const ref_CheckPointLimit = ref(null)
const point_limit = reactive({
  is_over: false,
})

// ==== match score color ====
const matchScoreClass = (score) => {
  if (score === null || score === undefined) return ''
  if (score >= 70) return 'text-success fw-bold'
  if (score >= 40) return 'text-warning fw-bold'
  return 'text-danger'
}

// ==== status badge ====
const statusBadgeClass = (status) => {
  if (status === 'added') return 'badge-status badge-added'
  if (status === 'dismissed') return 'badge-status badge-dismissed'
  return 'badge-status badge-new'
}
const statusLabel = (status) => {
  if (status === 'added') return t("Crawl_articles.status_added")
  if (status === 'dismissed') return t("Crawl_articles.status_dismissed")
  return t("Crawl_articles.status_new")
}

</script>

<template>

  <!-- isLoading -->
  <LoadingIcon :is-loading="isLoading.show"></LoadingIcon>

  <!-- modal_over_points -->
  <CheckPointLimit ref="ref_CheckPointLimit"></CheckPointLimit>

  <!-- Modal_update (Edit) -->
  <div v-if="modal_update.show" class="modal-create">
    <div class="modal-content">

      <div class="row">
        <h4>{{ $t("Crawl_articles.modal_edit_title") }}</h4>
      </div>

      <div class="row" style="margin-top: 20px">
        <label>{{ $t("Crawl_articles.title_label") }}</label>
        <input type="text" class="form-control" style="margin-top: 5px"
               v-model="modal_update.article.title"/>
      </div>

      <div class="row" style="margin-top: 15px">
        <label>{{ $t("Crawl_articles.url_label") }}</label>
        <input type="text" class="form-control" style="margin-top: 5px"
               v-model="modal_update.article.url"/>
      </div>

      <div class="row" style="margin-top: 20px">
        <label>{{ $t("Crawl_articles.match_score_label") }}</label>
        <p style="margin-top: 5px; font-size: 1.1em; font-weight: bold;">
          {{ modal_update.article.match_score !== null ? modal_update.article.match_score + '%' : $t("Crawl_articles.not_judged") }}
        </p>
      </div>

      <div class="row" style="margin-top: 10px">
        <label>{{ $t("Crawl_articles.match_reason_label") }}</label>
        <p style="margin-top: 5px; white-space: pre-wrap; padding: 10px; background: #f8f9fa; border-radius: 4px;">
          {{ modal_update.article.match_reason || $t("Crawl_articles.not_judged") }}
        </p>
      </div>

      <!-- buttons -->
      <div class="row" style="margin-top: 30px; gap: 10px;">
        <button class="btn btn-primary" style="width: 120px"
                @click="handleEdit_confirm">
          {{ $t("Crawl_articles.update") }}</button>

        <button class="btn btn-primary" style="width: 150px"
                v-if="modal_update.article.status !== 'added'"
                @click="handleAddSingleToArticles">
          {{ $t("Crawl_articles.add_to_articles") }}</button>

        <button class="btn btn-warning" style="width: 120px"
                v-if="modal_update.article.status === 'new'"
                @click="handleDismissFromModal">
          {{ $t("Crawl_articles.dismiss") }}</button>

        <button class="btn btn-danger" style="width: 120px"
                @click="handleDeleteFromModal">
          {{ $t("Crawl_articles.delete") }}</button>

        <button class="btn btn-secondary" style="width: 120px"
                @click="modal_update.show=false">
          {{ $t("Crawl_articles.back") }}</button>
      </div>

    </div>
  </div>

  <!-- modal_del_1 -->
  <div v-if="modal_del_1.show" class="modal-del">
    <div class="modal-content">
      <div class="row" style="margin-top: 20px;text-align: center">
        <p>{{ modal_del_1.msg }}</p>
      </div>
      <div class="row" style="margin-top: 20px;margin-left: 10px; margin-right: 10px">
        <button class="btn btn-primary" @click="handleDelete_confirm">OK</button>
      </div>
      <div class="row" style="margin-top: 20px;margin-left: 10px; margin-right: 10px">
        <button class="btn btn-primary" @click="modal_del_1.show = false">{{ $t("Crawl_articles.cancel") }}</button>
      </div>
    </div>
  </div>

  <div class="container">

    <!-- title -->
    <div class="row">
      <h4>{{ $t("Crawl_articles.title") }}</h4>
    </div>

    <!-- status tabs -->
    <div class="row" style="margin-top: 10px">
      <div class="status-tabs">
        <button v-for="tab in STATUS_TABS" :key="tab.value"
                class="status-tab"
                :class="{ active: filter.status === tab.value }"
                @click="handleStatusTab(tab.value)">
          {{ tab.label }}
        </button>
      </div>
    </div>

    <!-- filters -->
    <div class="row">
      <div class="column">
        <label>{{ $t("Crawl_articles.keyword") }}</label>
        <input type="text" v-model="filter.search_keyword_temp"
               style="padding: 0 10px; margin: 10px;"
               @keyup.enter="handleFilter"/>
      </div>
    </div>
    <div class="row">
      <div class="column">
        <label>{{ $t("Crawl_articles.source_site_filter") }}</label>
        <select v-model="filter.crawl_site_id" @change="handleSiteFilter"
                style="padding: 0 10px; margin: 10px;">
          <option :value="null">{{ $t("Crawl_articles.status_all") }}</option>
          <option v-for="site in crawl_sites_list" :key="site.id" :value="site.id">
            {{ site.site_name }}
          </option>
        </select>
      </div>
    </div>

    <!-- action buttons -->
    <div class="row">
      <div class="column" style="text-align: right">

        <button class="btn btn-primary"
                @click="handleBulkAdd"
                style="padding: 0 30px; margin: 10px;"
                v-if="filter.status === 'new' || filter.status === 'all'">
          + {{ $t("Crawl_articles.add_to_articles") }}</button>

        <button class="btn btn-outline-warning"
                @click="handleBulkDismiss"
                style="padding: 0 30px; margin: 10px;"
                v-if="filter.status === 'new' || filter.status === 'all'">
          {{ $t("Crawl_articles.bulk_dismiss") }}</button>

        <button class="btn btn-outline-danger"
                @click="handleBulkDelete"
                style="padding: 0 30px; margin: 10px;">
          {{ $t("Crawl_articles.bulk_delete") }}</button>

        <button class="btn btn-outline-primary"
                @click="handleRefresh"
                style="padding: 0 30px; margin: 10px;">
          {{ $t("Crawl_articles.refresh") }}</button>

      </div>
    </div>

    <!-- select/de-select -->
    <div class="row">
      <div class="column">
        <input id="select_all" type="checkbox" @change="onSelectAllChange"/>
        <label style="padding: 0 10px; margin: 1px;" for="select_all">
          {{ $t("Crawl_articles.select_all") }}</label>
        <span style="color: #888; font-size: 12px; margin-left: 10px;">
          {{ $t("Crawl_articles.total_count", { count: data.total_count }) }}
        </span>
      </div>
    </div>

    <div class="row">

      <!-- the main table -->
      <table class="table table-hover crawl-table">
        <colgroup>
          <col style="width: 40px;">    <!-- 対象 -->
          <col style="width: 120px;">   <!-- サイト名 -->
          <col style="width: 35%;">     <!-- タイトル -->
          <col style="width: 25%;">     <!-- URL -->
          <col style="width: 85px;">    <!-- 作成日 -->
          <col style="width: 70px;">    <!-- マッチ度 -->
          <col style="width: 70px;">    <!-- 状態 -->
          <col style="width: 160px;">   <!-- 操作 -->
        </colgroup>
        <thead>
        <tr>
          <th scope="col">{{ $t("Crawl_articles.target") }}</th>
          <th scope="col">{{ $t("Crawl_articles.site_name") }}</th>
          <th scope="col">{{ $t("Crawl_articles.article_title") }}</th>
          <th scope="col">{{ $t("Crawl_articles.url") }}</th>
          <th scope="col">{{ $t("Crawl_articles.created_at") }}</th>
          <th scope="col">{{ $t("Crawl_articles.match_score") }}</th>
          <th scope="col">{{ $t("Crawl_articles.status") }}</th>
          <th scope="col">{{ $t("Crawl_articles.operation") }}</th>
        </tr>
        </thead>

        <tbody>
          <tr v-for="item in data.items" :key="item.id"
              style="vertical-align: middle">

            <td style="text-align: center;">
              <input type="checkbox" v-model="checked.items[item.id]">
            </td>

            <td class="td-truncate" :title="item.site_name">
              {{ item.site_name }}
            </td>

            <td class="td-truncate" :title="item.title">
              {{ item.title }}
            </td>

            <td class="td-truncate">
              <a :href="item.url" target="_blank" :title="item.url">
                {{ item.url }}
              </a>
            </td>

            <td style="white-space: nowrap;">
              {{ datePipe(item.created_at, format = 'yyyy/MM/dd') }}
            </td>

            <td style="text-align: center;" :class="matchScoreClass(item.match_score)">
              {{ item.match_score !== null ? item.match_score + '%' : '-' }}
            </td>

            <td style="text-align: center;">
              <span :class="statusBadgeClass(item.status)">{{ statusLabel(item.status) }}</span>
            </td>

            <td style="white-space: nowrap;">
              <button class="btn btn-primary btn-sm" style="padding: 0 10px"
                      @click="handleEdit(item.id)">
                {{ $t("Crawl_articles.edit") }}</button>

              <button class="btn btn-warning btn-sm" style="padding: 0 8px; margin-left: 4px"
                      v-if="item.status === 'new'"
                      @click="handleDismiss(item.id)">
                {{ $t("Crawl_articles.dismiss") }}</button>

              <button class="btn btn-danger btn-sm" style="padding: 0 8px; margin-left: 4px"
                      @click="handleDelete(item.id, item.title)">
                {{ $t("Crawl_articles.delete") }}</button>
            </td>

          </tr>
        </tbody>
      </table>

    </div>
    <div class="row align-items-center" style="margin-top: 8px;">
      <div class="col-auto" style="display: flex; align-items: center; gap: 8px;">
        <label style="font-size: 13px; color: #555; white-space: nowrap; margin: 0;">{{ $t("Crawl_articles.page_size") }}</label>
        <select v-model="data.page_num" @change="handlePageNumChange"
                style="padding: 2px 8px; font-size: 13px; border: 1px solid #dee2e6; border-radius: 4px;">
          <option v-for="n in PAGE_NUM_OPTIONS" :key="n" :value="n">{{ $t("Crawl_articles.page_size_option", { n }) }}</option>
        </select>
      </div>
      <div class="col">
        <paginate style="cursor: pointer; margin: 0;"
                  v-model="data.current_page"
                  :page-count="page_count"
                  :page-range="3"
                  :margin-pages="2"
                  :prev-text="'<'"
                  :next-text="'>'"
        >
        </paginate>
      </div>
    </div>
  </div>

</template>

<style scoped>
.status-tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 8px;
}
.status-tab {
  padding: 4px 16px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  font-size: 13px;
  color: #555;
}
.status-tab.active {
  background: #0d6efd;
  color: #fff;
  border-color: #0d6efd;
}

.badge-status {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
}
.badge-new      { background: #e8f4fd; color: #0d6efd; }
.badge-added    { background: #d1e7dd; color: #198754; }
.badge-dismissed { background: #f8d7da; color: #dc3545; }

/* ===== crawl articles table ===== */
.crawl-table {
  table-layout: fixed;
  width: 100%;
}

.crawl-table th,
.crawl-table td {
  vertical-align: middle;
  overflow: hidden;
}

.crawl-table th {
  white-space: nowrap;
}

/* テキスト省略セル */
.td-truncate {
  max-width: 0;          /* table-layout: fixed と組み合わせて効かせる */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.td-truncate a {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: inherit;
}

.modal-del {
  height: 800px;
  max-height: 1024px;
  width: 480px;
  display: flex;
  justify-content: center;
  align-items: center;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0);
  z-index: 1000;
  top: 100px;
  left: 100px;
  position: absolute;
  padding: 20px;
}

.modal-create {
  height: 960px;
  max-height: 1280px;
  width: 960px;
  display: flex;
  justify-content: center;
  align-items: center;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0);
  z-index: 1000;
  top: 100px;
  left: 100px;
  position: absolute;
  padding: 20px;
}

.modal-content {
  background-color: #fff;
  padding: 20px;
  border-radius: 5px;
  max-width: 1600px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}
</style>
